import React from 'react';
import { Box, Header, SpaceBetween, Grid } from '@amzn/awsui-components-core';
import { BarLineChartWithProps } from '../fintech-ops-charts/BarChartWithProps';
import { EmptyStateMessage } from '../EmptyStateMessage';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { ErrorContainer } from '../FinTechOpsCommonElements';
import { FinTechOpsCategories } from '../FinTechOpsConstants';
import { BusinessReportingCustomPromptSummary } from './BusinessReportingCustomPromptSummary';
import { FinTechOpsMessages, GRAPH_WIDTHS_BREAK_POINTS, GRAPH_WIDTHS_BREAK_POINTS_2_GRAPHS } from '../FinTechOpsConstants';
import { i18nStringHour } from '../i18nStrings';
import { BusinessReportingFilters } from './BusinessReportingSelectionFilters';
import { BusinessReportingResolutionTypePieChart } from './BusinessReportingResolutionTypePieChart';
import { ProcessedBusinessReportingData } from './BusinessReportingTicketDataProcessing';
import { ProcessedBusinessReportingUserData } from './BusinessReportingUserDataProcessing';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

interface BusinessReportingTicketSectionProps {
  processedTicketData: ProcessedBusinessReportingData;
  processedUserData?: ProcessedBusinessReportingUserData;
  businessFilters: BusinessReportingFilters;
  originalFilterOptions: string;
  isLoading: boolean;
}

export const BusinessReportingTicketSection: React.FC<BusinessReportingTicketSectionProps> = ({
  processedTicketData,
  processedUserData,
  businessFilters,
  originalFilterOptions,
  isLoading
}) => {
  const renderContent = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    // Check if we have processed data - if not, show empty state
    if (!processedTicketData || processedTicketData.aggregatedData.entries.length === 0) {
      return <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />;
    }

    return (
      <SpaceBetween size="s">
        {/* AI Summary Box with Custom Prompt */}
        <BusinessReportingCustomPromptSummary
          title={`${Titles.TITLE_AI_SUMMARY}`}
          pageType={FinTechOpsCategories.businessTickets}
          statusKey="finished"
          statusValue="finished"
          filters={processedTicketData.processedFilters}
          aggregatedData={JSON.stringify(processedTicketData.aggregatedData)}
          processedTicketData={processedTicketData}
          processedUserData={processedUserData}
          businessFilters={businessFilters}
          originalFilterOptions={originalFilterOptions}
          isLoading={isLoading}
        />

        {/* Charts Grid */}
        <Grid
          gridDefinition={[
            { colspan: GRAPH_WIDTHS_BREAK_POINTS },
            { colspan: GRAPH_WIDTHS_BREAK_POINTS },
            { colspan: GRAPH_WIDTHS_BREAK_POINTS },
            { colspan: GRAPH_WIDTHS_BREAK_POINTS_2_GRAPHS },
            { colspan: GRAPH_WIDTHS_BREAK_POINTS_2_GRAPHS }
          ]}
        >
          {/* Tickets by Severity Chart */}
          <BarLineChartWithProps
            series={processedTicketData.severityChartData}
            xDomain={processedTicketData.severityChartXDomain}
            yDomain={[0, processedTicketData.severityChartYDomain]}
            domainType="string"
            xScaleType="categorical"
            arialLabel="Total Tickets with Severity and Resolution Count"
            errorText={FinTechOpsMessages.errorText}
            hideLegend={false}
            stackedBars={true}
            hideFilter={true}
            emptyMessage={
              <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
            }
            xTitle="Months"
            yTitle="No. of Tickets"
            i18nStrings={i18nStringHour('Severity')}
            noMatchMessage={FinTechOpsMessages.noMatchMessage}
            headerName="Total Tickets with Severity and Resolution Count"
          />

          {/* Tickets by Application Chart */}
          <BarLineChartWithProps
            series={processedTicketData.applicationChartData}
            xDomain={processedTicketData.applicationChartXDomain}
            yDomain={processedTicketData.applicationChartYDomain}
            domainType="string"
            xScaleType="categorical"
            arialLabel="Top 5 Applications by Ticket Count"
            errorText={FinTechOpsMessages.errorText}
            hideLegend={false}
            hideFilter={true}
            emptyMessage={
              <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
            }
            xTitle="Months"
            yTitle="No. of Tickets"
            i18nStrings={i18nStringHour('Application')}
            noMatchMessage={FinTechOpsMessages.noMatchMessage}
            headerName="Top 5 Applications by Ticket Count"
          />

          {/* Incoming Tickets by Month Chart */}
          <BarLineChartWithProps
            series={processedTicketData.incomingChartData}
            xDomain={processedTicketData.incomingChartXDomain}
            yDomain={processedTicketData.incomingChartYDomain}
            domainType="string"
            xScaleType="categorical"
            arialLabel="Incoming Tickets by Month"
            errorText={FinTechOpsMessages.errorText}
            hideLegend={false}
            stackedBars={false}
            hideFilter={true}
            emptyMessage={
              <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
            }
            xTitle="Months"
            yTitle="No. of Tickets"
            i18nStrings={i18nStringHour('Status')}
            noMatchMessage={FinTechOpsMessages.noMatchMessage}
            headerName="Incoming Tickets by Month"
          />

          {/* Top 5 Resolution Types by Ticket Count Pie Chart */}
          <BusinessReportingResolutionTypePieChart
            data={processedTicketData.resolutionTypeCountPieData}
            title="Top 5 Resolution Types by Ticket Count"
          />

          {/* Top 5 Resolution Types by Time Spent Pie Chart */}
          <BusinessReportingResolutionTypePieChart
            data={processedTicketData.resolutionTypeTimeSpentPieData}
            title="Top 5 Resolution Types by Time Spent"
            variant="donut"
            isTimeSpentChart={true}
          />
        </Grid>
      </SpaceBetween>
    );
  };

  return (
    <div>
      <Header variant="h2">{Titles.TITLE_TICKETS}</Header>
      {renderContent()}
    </div>
  );
};
